/* Progressive enhancement for the shared editorial product template.
   Description content stays in Shopify; Buy now is a plain form that opens the
   product in the store, ?view=store (setupBuy). No scroll interception or animation gate. */
(function () {
  if (customElements.get('sv-product-editorial')) return;
  customElements.define('sv-product-editorial', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.variants = JSON.parse(this.querySelector('[data-pdp-variants]').textContent);
      this.form = this.querySelector('form[data-type="buy-in-store-form"]');
      this.picker = this.querySelector('[data-pdp-variant]');
      this.dialog = this.querySelector('[data-pdp-dialog]');
      this.setupDetails(signal);
      this.querySelectorAll('[data-pdp-copy]').forEach(button => { button.hidden = false; });
      const nativeShare = this.querySelector('[data-pdp-share]');
      if (nativeShare) nativeShare.hidden = typeof navigator.share !== 'function';
      this.updateShareLinks();
      const colourOptions = this.querySelector('[data-pdp-colours]');
      if (colourOptions) {
        colourOptions.hidden = false;
        this.querySelector('[data-pdp-variant-fallback]').hidden = true;
      }
      this.addEventListener('click', event => this.onClick(event), { signal });
      this.picker?.addEventListener('change', () => this.setVariant(this.picker.value), { signal });
      this.dialog.addEventListener('click', event => { if (event.target === this.dialog) this.dialog.close(); }, { signal });
      this.prepareDescription();
      this.decorate(signal);
      this.setupBuy(signal);
      this.setupFaq(signal);
      // The footer folds the final native block, never the custom element or
      // an ancestor of the horizontal sequence's independent pin.
      const foldTarget = this.querySelector('.sv-pdp__related') || this.querySelector('.sv-pdp__reading--faq') || this.querySelector('[data-pdp-story]')?.lastElementChild || this.querySelector('[data-pdp-purchase]');
      foldTarget?.setAttribute('data-sv-fold-target', '');
      this.motion = window.SuvornaProductMotion?.create(this);
      this.gallery = window.SuvornaProductGallery?.create(this);
    }
    icon(name) {
      const source = this.querySelector(`template[data-pdp-icon="${name}"]`);
      return source ? source.content.firstElementChild.cloneNode(true) : null;
    }
    // Glyphs (Ahtisam, 12 September 2026). The cart rides the Buy now button (rollCart, started by
    // setupBuy). The list, key and page glyphs sit inside the Features, Tech Specs and Description
    // headings (before the text, as separate elements, so the word masks and the chapter blur
    // leave them alone) and draw themselves in once scrolled into view; hovering replays them.
    decorate(signal) {
      const reduced = matchMedia('(prefers-reduced-motion: reduce)');
      const button = this.querySelector('.product-form__submit'), form = this.form, cart = this.icon('cart');
      if (button && form && cart) {
        const glyph = document.createElement('i'); glyph.className = 'sv-pdp__cart'; glyph.setAttribute('aria-hidden', 'true'); glyph.append(cart);
        button.prepend(glyph);
        let current = null;
        const roll = (from, to, duration, easing) => {
          current?.cancel();
          current = glyph.animate([{ transform: `translateX(${from}px)` }, { transform: `translateX(${to}px)` }], { duration, easing, fill: 'forwards' });
          return current.finished;
        };
        const settle = () => { current?.cancel(); current = null; delete button.dataset.svAdding; };
        this.settleCart = settle;
        // One pass, whatever size the button is (Ahtisam, 10 October 2026): the trolley rolls out
        // through the right edge, the words stepping aside; `away` runs once it is gone (setupBuy
        // opens the store's page then), and it comes back in from the left and stops while that
        // page loads, never looping. Distances are measured against the button itself: in the
        // dock the trolley shares a wrapper with the words.
        this.rollCart = async away => {
          settle();
          button.dataset.svAdding = '';
          if (!reduced.matches) {
            const b = button.getBoundingClientRect(), g = glyph.getBoundingClientRect();
            const exit = b.right - g.left + 2, enter = -(g.right - b.left + 2);
            await roll(0, exit, 700, 'cubic-bezier(.55,0,.9,.45)').catch(() => {});
            away();
            await roll(enter, 0, 600, 'cubic-bezier(.16,1,.3,1)').catch(() => {});
          } else away();
          settle();
        };
        signal.addEventListener('abort', settle);
      }
      const story = this.querySelector('[data-pdp-story]');
      if (!story) return;
      // The Tech Specs panel carries a quiet loop on its right side (Ahtisam, 12 September 2026,
      // from the cognitra hero): a cropped copy of that video sits behind the panel, masked so
      // only the right part shows and the rows on the left stay on paper. Plays only in view.
      const specs = story.querySelector('#tech-specs');
      if (specs && this.dataset.specVideo && !reduced.matches && !specs.querySelector('.sv-pdp__spec-video')) {
        const box = document.createElement('div'), video = document.createElement('video');
        box.className = 'sv-pdp__spec-video'; box.setAttribute('aria-hidden', 'true'); box.append(video);
        video.muted = true; video.loop = true; video.playsInline = true; video.autoplay = true; video.preload = 'metadata';
        video.setAttribute('muted', ''); video.setAttribute('playsinline', ''); video.setAttribute('aria-hidden', 'true'); video.setAttribute('tabindex', '-1');
        video.src = this.dataset.specVideo;
        specs.prepend(box); specs.classList.add('sv-pdp__has-video');
        const watch = new IntersectionObserver(entries => entries.forEach(entry => { if (entry.isIntersecting) video.play().catch(() => {}); else video.pause(); }), { rootMargin: '20% 0px' });
        watch.observe(specs); signal.addEventListener('abort', () => { watch.disconnect(); box.remove(); specs.classList.remove('sv-pdp__has-video'); });
      }
      const features = [...story.querySelectorAll('#features > .grid__item > h2')].find(heading => !heading.closest('#tech-specs'));
      const description = [...story.querySelectorAll('h2, h3, h4, .sv-pdp__legacy-heading')].find(heading => /^(product\s+)?description$/i.test(heading.textContent.trim()));
      const marks = [];
      [[features, 'features'], [story.querySelector('#tech-specs h2'), 'specs'], [description, 'description']].forEach(([heading, name]) => {
        const svg = heading && this.icon(name);
        if (svg) { heading.prepend(svg); heading.dataset.pdpGlyph = name; marks.push({ heading, svg }); }
      });
      if (!marks.length) return;
      const observer = new IntersectionObserver(entries => entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-in'); observer.unobserve(entry.target);
      }), { threshold: .6, rootMargin: '0px 0px -12% 0px' });
      marks.forEach(({ heading, svg }) => {
        observer.observe(svg);
        heading.addEventListener('pointerenter', () => {
          if (!svg.classList.contains('is-in')) return;
          svg.classList.remove('is-in'); void svg.getBoundingClientRect(); svg.classList.add('is-in');
        }, { signal });
      });
      signal.addEventListener('abort', () => observer.disconnect());
    }
    disconnectedCallback() { this.detailsCleanup?.(); this.gallery?.dispose(); this.gallery = null; this.motion?.dispose(); this.motion = null; this.controller?.abort(); this.controller = null; }
    announce(message) { this.querySelector('[data-pdp-status]').textContent = message; }
    // Buy now (Ahtisam, 10 October 2026): opens this product in the store on the chosen variant
    // (the form's own address: ?view=store&variant=...), where it goes into the cart. The trolley
    // rolls out first (rollCart) and the store's page is asked for as it leaves. Back from the
    // store restores this page from the back/forward cache with the roll stopped and the words in place.
    setupBuy(signal) {
      const form = this.form, button = form?.querySelector('.product-form__submit');
      if (!button) return;
      let busy = false;
      const go = () => form.submit();
      form.addEventListener('submit', async event => {
        event.preventDefault();
        if (button.disabled || busy) return;
        busy = true;
        this.announce('Opening ' + (this.querySelector('h1')?.textContent.trim() || 'this product') + ' in the shop.');
        await (this.rollCart ? this.rollCart(go) : go());
        busy = false;
      }, { signal });
      window.addEventListener('pageshow', event => {
        if (!event.persisted) return;
        busy = false;
        this.settleCart?.();
        this.announce('');
      }, { signal });
    }
    // "Questions, answered." (the website's accordion): one answer open at a time, its height
    // eased by GSAP, the plus turning into an x. Without GSAP the native details still work.
    setupFaq(signal) {
      const list = this.querySelector('.sv-pdp__faq-list');
      if (!list || !window.gsap) return;
      const items = [...list.querySelectorAll('.sv-pdp__faq-item')];
      const reduced = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
      const answer = item => {
        const wrapped = item.querySelector(':scope > .sv-pdp__faq-answer');
        if (wrapped) return wrapped;
        const p = item.querySelector(':scope > p');
        if (!p) return null;
        const box = document.createElement('div'); box.className = 'sv-pdp__faq-answer';
        p.before(box); box.append(p);
        if (!item.open) box.style.height = '0px';
        return box;
      };
      items.forEach(answer);
      // Heights below the list change: the page's scroll positions are measured again.
      const settled = () => this.dispatchEvent(new Event('pdp:details-settled'));
      const close = item => {
        const box = answer(item);
        if (!box || reduced()) { item.open = false; if (box) box.style.height = '0px'; settled(); return; }
        gsap.to(box, { height: 0, duration: .42, ease: 'power2.inOut', overwrite: 'auto', onComplete: () => { item.open = false; settled(); } });
      };
      const open = item => {
        const box = answer(item);
        item.open = true;
        if (!box || reduced()) { if (box) box.style.height = 'auto'; settled(); return; }
        gsap.fromTo(box, { height: 0 }, { height: 'auto', duration: .5, ease: 'power2.inOut', overwrite: 'auto', clearProps: 'height', onComplete: settled });
      };
      list.addEventListener('click', event => {
        const summary = event.target.closest('.sv-pdp__faq-item > summary');
        if (!summary) return;
        event.preventDefault();
        const item = summary.parentElement, wasOpen = item.open;
        items.forEach(other => { if (other !== item && other.open) close(other); });
        if (wasOpen) close(item); else open(item);
      }, { signal });
    }
    setupDetails(signal) {
      const details = this.querySelector('.sv-pdp__identification'), summary = details?.querySelector('summary');
      if (!summary) return;
      let tween, expanded = details.open;
      const settle = () => {
        details.open = expanded;
        details.style.removeProperty('height'); details.style.removeProperty('overflow');
        delete details.dataset.pdpAnimating;
        delete details.dataset.pdpExpanded;
        summary.setAttribute('aria-expanded', String(expanded));
        this.dispatchEvent(new Event('pdp:details-settled'));
      };
      summary.addEventListener('click', event => {
        if (!window.gsap || matchMedia('(prefers-reduced-motion: reduce)').matches) return;
        event.preventDefault(); event.stopImmediatePropagation();
        if (!tween?.isActive()) expanded = details.open;
        expanded = !expanded;
        const start = details.getBoundingClientRect().height;
        tween?.kill();
        details.dataset.pdpAnimating = 'true';
        details.dataset.pdpExpanded = String(expanded);
        summary.setAttribute('aria-expanded', String(expanded));
        // Keep native content rendered until closing finishes. Measure in the
        // current layout, then animate in rem so site scaling is preserved.
        details.open = true; details.style.height = 'auto';
        const style = getComputedStyle(details), rem = parseFloat(getComputedStyle(document.documentElement).fontSize);
        const end = expanded ? details.getBoundingClientRect().height : summary.getBoundingClientRect().height + parseFloat(style.borderTopWidth) + parseFloat(style.borderBottomWidth);
        details.style.height = start / rem + 'rem'; details.style.overflow = 'hidden';
        tween = gsap.to(details, { height: end / rem + 'rem', duration: .42, ease: 'power2.inOut', overwrite: true, onComplete: settle });
      }, { capture: true, signal });
      const reduced = matchMedia('(prefers-reduced-motion: reduce)');
      reduced.addEventListener('change', () => { if (reduced.matches && tween?.isActive()) { tween.kill(); settle(); } }, { signal });
      this.detailsCleanup = () => { if (tween?.isActive()) { tween.kill(); settle(); } };
    }
    showMedia(id, options = {}) {
      const target = this.querySelector(`[data-pdp-media="${id}"]`);
      if (!target) return;
      this.querySelectorAll('[data-pdp-media]').forEach(media => {
        media.hidden = media !== target;
        if (media.hidden) media.querySelectorAll('video').forEach(video => video.pause());
      });
      this.querySelectorAll('[data-pdp-thumb]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.pdpThumb === String(id))));
      this.gallery?.sync(id);
      this.updateShareLinks();
      return this.motion?.setOpeningImage(target.dataset.mediaType === 'image' ? target.querySelector('img') : null, options) || false;
    }
    setVariant(id) {
      const variant = this.variants.find(item => String(item.id) === String(id));
      if (!variant) return;
      if (this.picker) this.picker.value = String(variant.id);
      this.querySelectorAll('[data-pdp-colour]').forEach(choice => choice.setAttribute('aria-pressed', String(choice.dataset.pdpColour === String(variant.id))));
      const selectedLabel = this.querySelector('[data-pdp-selected-colour]');
      if (selectedLabel) selectedLabel.textContent = variant.title;
      const form = this.form;
      form.querySelector('[name="variant"]').value = variant.id;
      const button = form.querySelector('[type="submit"]');
      button.disabled = !variant.available;
      button.removeAttribute('aria-disabled');
      // The label: inside the squeeze wrapper once the dock has dressed the button (motion), else a direct child.
      const label = button.querySelector('.sv-pdp__squeeze-inner > span') || button.querySelector(':scope > span');
      label.textContent = variant.available ? this.dataset.addLabel : this.dataset.soldLabel;
      // Only an in-stock variant travels to the dock.
      this.motion?.setDockAllowed?.(variant.available);
      // Out of stock: one plain price, the price before any saving; no was-price or
      // saving (as the Liquid renders it).
      const sale = variant.available && variant.previous > variant.price;
      const shown = !variant.available && variant.previous > variant.price ? variant.was : variant.money;
      const now = this.querySelector('[data-pdp-now]'), was = this.querySelector('[data-pdp-was]'), saving = this.querySelector('[data-pdp-saving]');
      now.textContent = shown; now.classList.toggle('is-sale', sale);
      const openingPrice = this.querySelector('[data-pdp-opening-price]');
      if (openingPrice) openingPrice.textContent = shown;
      was.textContent = variant.was; was.hidden = !sale;
      saving.hidden = !sale; saving.textContent = sale ? `Save ${Math.round((variant.previous - variant.price) / variant.previous * 100)}%` : '';
      const availability = this.querySelector('[data-pdp-availability]');
      availability.dataset.available = String(variant.available);
      availability.textContent = variant.available ? 'Available' : 'Currently sold out';
      for (const key of ['sku', 'barcode']) {
        this.querySelector(`[data-pdp-${key}]`).textContent = variant[key] || '';
        this.querySelector(`[data-pdp-${key}-row]`).hidden = !variant[key];
      }
      if (variant.media) this.showMedia(variant.media);
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      history.replaceState(history.state, '', url.href);
      this.updateShareLinks();
      this.announce(variant.title + ' selected. ' + variant.money + '. ' + (variant.available ? 'Available.' : 'Currently sold out.'));
    }
    shareData() {
      const url = new URL(this.dataset.productUrl, location.origin);
      url.searchParams.set('variant', this.form.querySelector('[name="variant"]').value);
      return { title: this.querySelector('[data-pdp-sharing]')?.dataset.shareTitle || this.querySelector('h1').textContent, url: url.href };
    }
    updateShareLinks() {
      const sharing = this.querySelector('[data-pdp-sharing]');
      if (!sharing) return;
      const data = this.shareData(), image = this.querySelector('[data-pdp-media]:not([hidden]) img');
      const destinations = { facebook: 'https://www.facebook.com/sharer.php', twitter: 'https://x.com/intent/tweet', pinterest: 'https://pinterest.com/pin/create/button/' };
      sharing.querySelectorAll('[data-pdp-share-network]').forEach(link => {
        const network = link.dataset.pdpShareNetwork, target = new URL(destinations[network]);
        target.searchParams.set(network === 'facebook' ? 'u' : 'url', data.url);
        if (network === 'twitter') target.searchParams.set('text', data.title);
        if (network === 'pinterest') {
          if (!image) { link.hidden = true; return; }
          target.searchParams.set('media', image.currentSrc || image.src);
          target.searchParams.set('description', data.title);
          link.hidden = false;
        }
        link.href = target.href;
      });
      sharing.querySelector('[data-pdp-share-url]').value = data.url;
      sharing.querySelector('[data-pdp-share-status]').textContent = '';
    }
    async copyProductLink(instagram = false) {
      const field = this.querySelector('[data-pdp-share-url]'), status = this.querySelector('[data-pdp-share-status]');
      const data = this.shareData();
      try {
        await navigator.clipboard.writeText(data.url);
        field.hidden = true;
        status.textContent = instagram ? 'Link copied. Paste it into an Instagram message or story.' : 'Product link copied.';
      } catch (_) {
        field.value = data.url; field.hidden = false; field.focus({ preventScroll: true }); field.select();
        status.textContent = instagram ? 'Copy this link, then paste it into Instagram.' : 'Copy the selected product link below.';
      }
      this.dispatchEvent(new Event('pdp:share-settled'));
    }
    async onClick(event) {
      const colour = event.target.closest('[data-pdp-colour]');
      if (colour) { this.setVariant(colour.dataset.pdpColour); return; }
      const jump = event.target.closest('[data-pdp-jump]');
      if (jump) {
        const target = this.querySelector('[data-pdp-purchase]');
        if (target) {
          event.preventDefault();
          const offset = -9 * parseFloat(getComputedStyle(document.documentElement).fontSize);
          const lenis = window.SuvornaMotion?.lenis;
          if (lenis) { lenis.resize(); lenis.scrollTo(target, { offset, duration: 1.05 }); }
          else window.scrollTo({ top: target.getBoundingClientRect().top + window.scrollY + offset, behavior: 'auto' });
          const title = target.querySelector('.sv-pdp__product-title');
          title.setAttribute('tabindex', '-1'); title.focus({ preventScroll: true });
        }
        return;
      }
      const thumbnail = event.target.closest('[data-pdp-thumb]');
      if (thumbnail) { this.showMedia(thumbnail.dataset.pdpThumb); this.announce(thumbnail.getAttribute('aria-label')); return; }
      const zoom = event.target.closest('[data-pdp-zoom]');
      if (zoom && typeof this.dialog.showModal === 'function') {
        event.preventDefault();
        const image = this.querySelector('[data-pdp-zoom-image]');
        image.src = zoom.href; image.alt = zoom.querySelector('img').alt;
        if (!this.dialog.open) this.dialog.showModal();
        return;
      }
      if (event.target.closest('[data-pdp-close]')) { this.dialog.close(); return; }
      const copy = event.target.closest('[data-pdp-copy]');
      if (copy) { await this.copyProductLink(copy.dataset.pdpCopy === 'instagram'); return; }
      if (event.target.closest('[data-pdp-share]')) {
        try {
          if (navigator.share) await navigator.share(this.shareData());
          else await this.copyProductLink();
        } catch (error) { if (error.name !== 'AbortError') await this.copyProductLink(); }
      }
    }
    prepareDescription() {
      const story = this.querySelector('[data-pdp-story]');
      if (!story || story.dataset.prepared) return;
      story.dataset.prepared = 'true';
      story.querySelectorAll('[style]').forEach(element=>element.setAttribute('data-pdp-legacy-style',''));
      const media = 'img,video,iframe,model-viewer,svg,audio,object,embed,canvas';
      const meaningful = element => element.textContent.trim() || element.matches(media) || element.querySelector(media);
      const headings = 'h2,h3,h4,.sv-pdp__legacy-heading';
      const isDescription = element => /^(product\s+)?description$/i.test(element.textContent.trim());
      // Older imports use bold paragraphs or standalone strong tags as headings.
      story.querySelectorAll('p,div,strong').forEach(element => {
        const strong = element.querySelector(':scope > strong');
        if ((element.tagName === 'STRONG' && /^(product\s+)?(features|description)$/i.test(element.textContent.trim())) ||
            (strong && element.textContent.trim() === strong.textContent.trim() && !element.querySelector(media))) element.classList.add('sv-pdp__legacy-heading');
      });
      // An unclosed overview in imported HTML can contain the entire remaining
      // story. Split at known section boundaries, preserving the original nodes
      // and their order. Never flatten an unfamiliar mixed-content wrapper.
      const liftTail = element => {
        while (element.parentElement !== story) {
          const parent = element.parentElement, tail = [];
          for (let node = element; node; node = node.nextSibling) tail.push(node);
          parent.after(...tail);
        }
      };
      story.querySelectorAll('#features,#features-set').forEach(element => {
        if (element.parentElement.closest('#overview,#features')) liftTail(element);
      });
      const importedDescription = [...story.querySelectorAll(headings)].find(isDescription);
      if (importedDescription?.parentElement.closest('#overview,#features,#features-set')) liftTail(importedDescription);
      // Section labels alone are not content. Ignore them when deciding whether
      // an optional block has anything to display, but retain real copy/media.
      const hasContent = element => {
        const copy = element.cloneNode(true);
        copy.querySelectorAll(headings).forEach(heading => {
          if (/^(?:(?:product\s+)?(?:features|description)|tech(?:nical)?\s+spec(?:s|ifications)|overview|a closer look)[.!:]?$/i.test(heading.textContent.trim())) heading.remove();
        });
        return meaningful(copy);
      };
      story.querySelectorAll('#tech-specs,#features > .grid__item,#features,#overview,#features-set').forEach(element => {
        if (!hasContent(element)) element.remove();
      });
      story.querySelectorAll('.kit-includes-title').forEach(title => {
        const block = title.parentElement === story ? title : title.parentElement;
        const next = block.nextElementSibling;
        if (!next?.matches('#features-set,.grid') || !hasContent(next)) {
          title.remove(); if (!meaningful(block)) block.remove();
        }
      });
      // Remove vacant layout columns from inside out so the remaining column
      // can occupy the available width. Unknown text and image-only blocks stay.
      [...story.querySelectorAll('.grid,.grid__item')].reverse().forEach(element => {
        if (!meaningful(element)) element.remove();
      });
      story.querySelectorAll('#overview,#features').forEach(element => {
        element.classList.toggle('sv-pdp__single-column', element.children.length === 1);
      });
      [...story.children].forEach(element => {
        if (!meaningful(element) || element.tagName === 'META') element.hidden = true;
      });
      const storySection = story.closest('.sv-pdp__story-section');
      if (!hasContent(story)) {
        storySection.hidden = true;
        this.querySelectorAll('a[href]').forEach(link => { if (link.getAttribute('href') === '#' + storySection.id) link.hidden = true; });
        return;
      }
      story.querySelectorAll('img').forEach(image => {
        image.loading = 'lazy'; image.decoding = 'async';
      });
      // Kit descriptions sometimes repeat anonymous grids instead of using
      // #features-set. Recognise the same image/text structure without relying
      // on product handles or changing the original content order.
      story.querySelectorAll('.grid').forEach(grid => {
        const cards = [...grid.children].filter(child => child.classList.contains('grid__item'));
        if (cards.length && cards.every(card => {
          const parts = [...card.children].filter(child => child.classList.contains('grid__item'));
          return parts.some(part => part.querySelector('img')) && parts.some(part => !part.querySelector('img') && part.textContent.trim());
        })) grid.classList.add('sv-pdp__highlights');
      });
      // Reading stages are structural, so content stays accessible with or
      // without motion. Unknown descriptions keep their original flow.
      const makeReading = (element, name) => {
        if (!element || element.parentElement !== story) return;
        const stage = document.createElement('section');
        stage.className = 'sv-pdp__reading sv-pdp__reading--' + name;
        stage.dataset.pdpReading = name;
        const content = document.createElement('div');content.className = 'sv-pdp__reading-content';
        element.before(stage);stage.append(content);content.append(element);
        return {stage, content};
      };
      const overview = makeReading(story.querySelector('#overview'), 'overview');
      const heading = this.querySelector('.sv-pdp__story-section > .sv-pdp__section-heading');
      if (overview && heading) overview.content.prepend(heading);
      else if (heading) {
        heading.hidden = true;
        storySection.removeAttribute('aria-labelledby');
        storySection.setAttribute('aria-label', 'Product details');
      }
      const features = makeReading(story.querySelector('#features'), 'features');
      [overview, features].filter(Boolean).forEach(reading => {
        const divider = document.createElement('span');divider.className = 'sv-pdp__reading-divider';divider.setAttribute('aria-hidden','true');
        divider.innerHTML = '<i></i><b><svg viewBox="0 0 16 16" fill="none"><path d="M8 1v14M1 8h14" stroke="currentColor" stroke-width="1.2"/></svg></b>';
        reading.stage.prepend(divider);
      });
      const imageOnly = element => !element.textContent.trim() && element.querySelector('img') && !element.querySelector('video,iframe,table');
      // The description tail has its own ground in vertical and reduced-motion layouts too.
      const descriptionHeading = [...story.querySelectorAll(headings)].find(isDescription);
      if (descriptionHeading) {
        let first = descriptionHeading;
        while (first.parentElement && first.parentElement !== story) first = first.parentElement;
        if (first.parentElement === story && !first.querySelector('#overview, #features, #features-set, .sv-pdp__highlights')) {
          const description = document.createElement('div');description.className = 'sv-pdp__description';
          first.before(description);
          while (description.nextSibling) description.append(description.nextSibling);
          if (!hasContent(description)) description.remove();
        }
      }
      // Plain prose, including short descriptions and bare text nodes, needs
      // the same ground on mobile and without animation. No length threshold.
      if (!story.querySelector('.sv-pdp__description')) {
        const nodes = [...story.childNodes];
        let last = -1;
        nodes.forEach((node, index) => {
          if (node.nodeType === 1 && node.matches('.sv-pdp__reading,#features-set,.sv-pdp__highlights,.grid')) last = index;
        });
        const tail = nodes.slice(last + 1);
        if (tail.some(node => node.nodeType === 1 ? !node.hidden && meaningful(node) : node.nodeType === 3 && node.textContent.trim())) {
          const description = document.createElement('div');description.className = 'sv-pdp__description';
          tail[0].before(description);description.append(...tail);
        }
      }
      // Adjacent measurement photographs share a row. No heading or paragraph
      // is extracted, rewritten or dropped; unfamiliar structures remain intact.
      const children = [...story.children].filter(element => !element.hidden);
      for (let i = 0; i < children.length - 1; i++) {
        if (!imageOnly(children[i]) || !imageOnly(children[i + 1])) continue;
        const group = document.createElement('div'); group.className = 'sv-pdp__image-pair';
        children[i].before(group); group.append(children[i], children[i + 1]); i++;
      }
    }
  });
})();
